import { showErrorNotification } from '@Ravnur-Inc/notifications/service';
import { AxiosError } from 'axios';

import MediaJobProgressbar from '@/components/media/media-job-progressbar/media-job-progressbar';
import MediaUploadProgressbar from '@/components/media/media-upload-progressbar/media-upload-progressbar';
import ModalService from '@/helpers/modal.service';
import UploadService from '@/helpers/upload-service';
import MediaRepository from '@/repositories/media-repository';
import useJobsStore from '@/store/jobs';
import useUploadStore from '@/store/upload';
import { Job, Job$Status, Job$Type } from '@/types/Job';
import { Media$Details, Media$Type, Media$Version } from '@/types/Media';
import { UploadInfo, UploadStatus } from '@/types/Upload';
import dateToString from '@Ravnur-Inc/core/filters/date';

import './media-versions-table.scss';

import { $Props } from '@Ravnur-Inc/core/typings/tsx';
import { Options, setup, Vue } from 'vue-class-component';

const CN = 'media-versions-table';
const GONE_HTTP_STATUS = 410;
const repository = new MediaRepository();

class Props {
  media: Media$Details;
  list: Media$Version[];
}

class Emits {
  onRefreshVersions: () => void;
}

@Options({
  emits: ['refreshVersions'],
  inject: {
    uploadService: 'UPLOAD_SERVICE',
  },
})
export default class MediaVersionsTable extends Vue.with(Props) {
  declare $props: $Props<Props, Emits>;
  declare uploadService: UploadService;

  private jobs = setup(() => useJobsStore());
  private upload = setup(() => useUploadStore());
  private cachedCancelledJobs: Media$Version[] = [];

  loadingJobs: string[] = [];

  get job(): Nullable<Job> {
    const { id: mediaId } = this.media;
    return this.jobs.running[mediaId];
  }

  get runningJobs() {
    return this.jobs.running;
  }

  get allJobs() {
    return this.jobs.all;
  }

  get disabled() {
    return (
      !!this.job ||
      (this.uploadInfo && this.uploadInfo?.status !== UploadStatus.FINISHED) ||
      this.versions.some((v) => !!v.job && v.job?.state !== Job$Status.SUCCESS)
    );
  }

  get uploadInfo() {
    return this.upload.cacheByMediaId[this.media.id];
  }

  get cachedCancelledJobsVersionIdMap(): Record<string, Media$Version> {
    return this.cachedCancelledJobs.reduce(
      (acc, version) => ({
        ...acc,
        [version.id]: version,
      }),
      {}
    );
  }

  get versions(): Media$Version[] {
    const { list, uploadInfo } = this;
    const res: Media$Version[] = list.map((version) => {
      const cancelledVersion = this.cachedCancelledJobsVersionIdMap[version.id];
      return {
        ...version,
        job: cancelledVersion ? cancelledVersion.job : version.job,
      };
    });
    if (uploadInfo && uploadInfo.status !== UploadStatus.FINISHED) {
      res.push({
        id: 'FAKE_UPLOAD_ROW',
        isActive: false,
        createdDate: null,
        url: '',
        format: '',
        size: '',
        job: null,
        uploadInfo,
        isNotVOD: false,
      });
    }

    return res;
  }

  render() {
    return (
      <table class={CN}>
        <thead>
          <tr>
            <th class={`${CN}__th`}>
              <l10n group="media" tkey="download__version-header" />
            </th>
            <th class={`${CN}__th`}>
              <l10n group="media" tkey="download__format-header" />
            </th>
            <th class={[`${CN}__th`, `${CN}__th--right`]}>
              <l10n group="media" tkey="download__date-header" />
            </th>
            <th class={[`${CN}__th`, `${CN}__th--right`]}>
              <l10n group="media" tkey="download__size-header" />
            </th>
            <th class={[`${CN}__th`, `${CN}__th--right`]}>
              <l10n group="media" tkey="download__action-header" />
            </th>
          </tr>
        </thead>
        <tbody class={`${CN}__body`}>
          {this.versions.map((v, idx) => (
            <tr
              key={v.id}
              class={{
                [`${CN}__tr`]: true,
                [`${CN}__tr--processing`]: v.job || v.uploadInfo,
              }}
            >
              <td class={`${CN}__td`}>
                {idx ? (
                  <l10n group="media" params={{ num: idx }} tkey="download__n-version" />
                ) : (
                  <l10n group="media" tkey="download__o-version" />
                )}

                <badge class={`${CN}__badge`} mode="success" v-show={v.isActive}>
                  <l10n group="media" tkey="download__active-version" />
                </badge>

                {v.uploadInfo ? (
                  <MediaUploadProgressbar
                    class={`${CN}__progressbar`}
                    item={v.uploadInfo}
                    onCancel={() => v.uploadInfo && this.cancelUploading(v.uploadInfo)}
                    onPause={() => v.uploadInfo && this.pauseUploading(v.uploadInfo)}
                    onResume={() => v.uploadInfo && this.resumeUploading(v.uploadInfo)}
                    onRetry={() => v.uploadInfo && this.retryUploading(v.uploadInfo)}
                  />
                ) : null}

                {v.job ? (
                  <MediaJobProgressbar
                    class={`${CN}__progressbar`}
                    item={v.job}
                    loading={this.loadingJobs.includes(v.id)}
                    onCancel={() => this.cancel(v)}
                    onDelete={() => this.delete(v)}
                    onRetry={() => v.job && this.retry(v)}
                  />
                ) : null}
              </td>
              <td class={`${CN}__td`}>
                {v.isNotVOD
                  ? this.$t('media', 'download__is-not-vod-format')
                  : v.format.replace('.', '')}
              </td>
              <td class={[`${CN}__td`, `${CN}__td--right`]}>
                {v.createdDate ? dateToString(v.createdDate) : null}
              </td>
              <td class={[`${CN}__td`, `${CN}__td--right`]}>{v.size}</td>
              <td class={[`${CN}__td`, `${CN}__td--right`, `${CN}__td--actions`]}>
                <action-button
                  class={`${CN}__action`}
                  color="primary"
                  disabled={this.disabled}
                  icon={v.isActive ? 'refresh' : 'cached'}
                  process={() => this.onRetranscode(v)}
                  title={
                    v.isNotVOD
                      ? this.$t('media', 'download__convert-to-vod-asset')
                      : v.isActive
                      ? this.media.type === Media$Type.AUDIO
                        ? this.$t('media', 'download__retranscode-audio')
                        : this.$t('media', 'download__retranscode')
                      : this.$t('media', 'download__restore')
                  }
                  v-show={v.url}
                />

                <download-button
                  disabled={this.disabled}
                  name={v.format}
                  src={v.url}
                  title={this.$t('media', 'download__download')}
                  v-show={v.url && !v.isNotVOD}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  private async confirmRetranscode(version: Media$Version) {
    if (version.isNotVOD) {
      const msg = this.$t('media', 'download__convert-to-vod-asset-confirm');

      const flag = await ModalService.confirm(msg, {
        confirmText: this.$t('media', 'download__convert-to-vod-btn'),
      }).catch(() => false);

      return flag ? {} : Promise.reject(new Error(''));
    }

    if (version.isActive) {
      let msg = this.$t('media', 'download__retranscode-confirm');

      if (this.media.type === Media$Type.AUDIO) {
        msg = this.$t('media', 'download__retranscode-audio-confirm');
      }

      const flag = await ModalService.confirm(msg, {
        confirmText: this.$t('media', 'download__retranscode-btn'),
      }).catch(() => false);

      return flag ? {} : Promise.reject(new Error(''));
    }

    const confirmText = this.$t('media', 'download__revert-btn');
    const msg = this.$t('media', 'download__revert-confirm');
    const flag = await ModalService.confirm(msg, { confirmText }).catch(() => false);

    return flag ? {} : Promise.reject(new Error(''));
  }

  private async onRetranscode(version: Media$Version) {
    try {
      await this.confirmRetranscode(version);
    } catch (e) {
      return;
    }

    await this.retranscode(version);
  }

  private async retranscode(version: Media$Version) {
    this.setLoading(version.id);
    try {
      const { entityId } = await repository.process(this.media.id, version.id);
      if (!entityId) {
        return;
      }
      version.job = await this.jobs.add(entityId);
    } catch (e) {
      this.$processException(e);
    } finally {
      this.$emit('refreshVersions');
      this.unsetLoading(version.id);
    }
  }

  private async retry(version: Media$Version) {
    if (!version.job) {
      return;
    }
    this.setLoading(version.id);
    try {
      const result = await this.jobs.retry(version.job);
      if (!result?.data) {
        return;
      }
      version.job = result.data;
      this.cachedCancelledJobs = [];
    } catch (e) {
      this.$processException(e);
    } finally {
      this.$emit('refreshVersions');
      this.unsetLoading(version.id);
    }
  }

  private async cancel(version: Media$Version) {
    const { list } = this;
    const { job } = version;

    if (!job) {
      return;
    }

    this.setLoading(version.id);

    try {
      await this.jobs.cancel(job);
      const cancelledJob = this.jobs.log[this.media.id];
      if (cancelledJob.state === Job$Status.CANCELED) {
        this.cachedCancelledJobs.push({
          ...version,
          job: cancelledJob,
        });
      }
      // if we cancel the first transcoding job it causes a purge of the current media item
      if (job.type === Job$Type.TRANSCODING && list.length === 1) {
        this.$router.push({ name: 'Videos' });
      }
    } catch (e: unknown) {
      if ((e as AxiosError).response && (e as AxiosError).response?.status === GONE_HTTP_STATUS) {
        showErrorNotification(this.$t('common', 'notification_center__prevent_canceling_job', job));
      } else {
        this.$processException(e);
      }
    } finally {
      this.unsetLoading(version.id);
    }
  }

  private async delete(version: Media$Version) {
    if (!version.job) {
      return;
    }
    this.setLoading(version.id);
    try {
      await this.jobs.remove(version.job);
      this.cachedCancelledJobs = [];
    } catch (e: unknown) {
      this.$processException(e);
    } finally {
      this.unsetLoading(version.id);
    }
  }

  private setLoading(versionId: string) {
    this.loadingJobs.push(versionId);
  }

  private unsetLoading(versionId: string) {
    this.loadingJobs.splice(this.loadingJobs.indexOf(versionId), 1);
  }

  private resumeUploading(info: UploadInfo) {
    this.uploadService.resume(info);
  }

  private pauseUploading(info: UploadInfo) {
    this.uploadService.pause(info);
  }

  private retryUploading(info: UploadInfo) {
    this.uploadService.retry(info);
  }

  private async cancelUploading(info: UploadInfo) {
    this.pauseUploading(info);
    const msg = this.$t('media', 'download__abort-confirmer');
    const confirmText = this.$t('media', 'download__delete-btn');
    const flag = await ModalService.confirm(msg, { confirmText }).catch(() => false);

    if (!flag) {
      this.resumeUploading(info);
      return;
    }

    try {
      await this.uploadService.cancel(info, true);
      if (this.versions.length === 0) {
        this.$router.push({
          name: 'Videos',
        });
      }
    } catch (e) {
      this.$processException(e);
      this.pauseUploading(info);
    }
  }
}
